import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Star, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { devWarn } from "@/lib/uiStorage";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import { AnimatedSection, fadeInUp } from "@/components/layout/AnimatedSection";
import { PACKAGES, priceFor, monthlyEquivalent, costPerAd, formatMoney, CREDIT_COSTS } from "@/config/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  free: "from-slate-500 to-zinc-600",
  starter: "from-violet-500 to-indigo-500",
  creator: "from-violet-600 to-indigo-600",
  growth: "from-violet-600 to-purple-600",
  pro: "from-indigo-600 to-blue-700",
  agency: "from-violet-500 to-fuchsia-600",
};

export function BillingToggle({ interval, onChange }) {
  return (
    <div className="inline-flex items-center rounded-full bg-white/[0.05] ring-1 ring-white/10 p-1 text-sm" data-testid="billing-toggle">
      {["month", "year"].map((v) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`billing-toggle-${v}`} aria-pressed={interval === v}
          className={`px-4 py-1.5 rounded-full font-semibold transition-colors ${interval === v ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow" : "text-white/60 hover:text-white"}`}>
          {v === "month" ? "Monthly" : <>Annual <span className="ml-1 text-[11px] font-bold text-emerald-300/0 text-violet-200">· 2 months free</span></>}
        </button>
      ))}
    </div>
  );
}

function PriceBlock({ pkg, currency, interval, meta }) {
  if (pkg.id === "free") {
    return (
      <div className="mb-5">
        <span className="text-4xl font-extrabold text-white" data-testid="price-free">Free</span>
        <p className="text-white/45 text-xs mt-1">30 credits · no card</p>
      </div>
    );
  }
  const effInterval = pkg.annual ? interval : "month";
  const monthly = monthlyEquivalent(pkg, currency, effInterval);
  const total = priceFor(pkg, currency, effInterval);
  const perAd = costPerAd(pkg, currency, effInterval);
  return (
    <div className="mb-5">
      <div className="flex items-end gap-1 flex-wrap">
        <span className="text-4xl font-extrabold text-white" data-testid={`price-${pkg.id}`}>{formatMoney(effInterval === "year" ? Math.round(monthly) : monthly, currency)}</span>
        <span className="text-white/50 text-sm mb-1">/mo</span>
        <span className="text-violet-300/80 text-[11px] font-semibold mb-1.5 ml-1" data-testid={`tax-label-${pkg.id}`}>{meta.label}</span>
      </div>
      <p className="text-white/45 text-xs mt-1" data-testid={`billing-note-${pkg.id}`}>
        {effInterval === "year"
          ? <>Billed {formatMoney(total, currency)} a year · 2 months free</>
          : pkg.annual ? "Billed monthly · cancel any time" : "Monthly billing only · cancel any time"}
      </p>
      {perAd && <p className="text-white/60 text-xs mt-1.5 font-medium" data-testid={`cost-per-ad-${pkg.id}`}>{perAd} per finished ad</p>}
    </div>
  );
}

export function PricingSection() {
  const [loading, setLoading] = useState(null);
  const [interval, setInterval] = useState("month");
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency, meta, localeHint, region } = useCurrency();

  const cards = PACKAGES.filter((p) => p.id !== "enterprise");

  const handleCheckout = async (packageId) => {
    if (packageId === "free") { navigate(user ? "/studio" : "/register"); return; }
    const pkg = PACKAGES.find((p) => p.id === packageId);
    const effInterval = pkg?.annual ? interval : "month";
    if (!user) { navigate(`/register?plan=${packageId}&interval=${effInterval}`); return; }
    setLoading(packageId);
    try {
      let endpoint = `${API}/checkout/create`;
      try {
        const mode = await axios.get(`${API}/billing/mode`);
        if (mode?.data?.subscription_enabled) endpoint = `${API}/checkout/subscribe`;
      } catch (err) { devWarn("Billing mode lookup failed — falling back to one-off checkout", err); }
      const response = await axios.post(endpoint, { package_id: packageId, origin_url: window.location.origin, interval: effInterval, locale_hint: localeHint });
      if (response.data.url) window.location.href = response.data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to initiate checkout. Please try again."); }
    finally { setLoading(null); }
  };

  return (
    <AnimatedSection className="py-20 md:py-24 relative" id="pricing">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-purple-300 mb-4"><Star className="w-4 h-4" />Priced per finished ad, not per credit</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4"><span className="gradient-text">Pick how many ads you need each month.</span></h2>
          <p className="text-white/55 max-w-2xl mx-auto text-sm md:text-base" data-testid="pricing-subtitle">
            Every plan includes the full Studio. Plans from <span className="text-violet-300 font-semibold">{formatMoney(priceFor(PACKAGES[1], currency), currency)}/mo</span> · {meta.label} · Cancel any time.
          </p>
          <div className="mt-6 flex flex-col items-center gap-2">
            <BillingToggle interval={interval} onChange={setInterval} />
            {region === "AU" && <p className="text-white/40 text-xs" data-testid="aud-locked-note">Australian prices include GST and are billed in AUD.</p>}
          </div>
          {user?.winback && (
            <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full px-5 py-2.5 ring-1 ring-violet-400/40 bg-violet-500/10 text-sm" data-testid="pricing-winback-ribbon">
              <Sparkles className="w-4 h-4 text-violet-300" />
              <span className="text-white font-semibold">Welcome back — {user.winback.percent_off}% off for {user.winback.months} months</span>
              <span className="text-violet-300 font-mono text-xs tracking-widest">{user.winback.code}</span>
              <span className="text-white/50 text-xs">applied automatically at checkout</span>
            </div>
          )}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pricing-cards">
          {cards.map((pkg) => (
            <motion.div key={pkg.id} variants={fadeInUp} className={`relative rounded-3xl p-6 flex flex-col h-full card-hover ${pkg.popular ? "pricing-featured" : "glass"}`} data-testid={`pricing-card-${pkg.id}`}>
              {pkg.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2"><span className="px-4 py-2 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-bold shadow-lg whitespace-nowrap">Most Popular</span></div>}
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${TIER_COLOR[pkg.id]} flex items-center justify-center mb-4`}><Star className="w-6 h-6 text-white" /></div>
              <h3 className="text-xl font-bold mb-1 text-white">{pkg.name}</h3>
              <p className="text-white/50 text-sm mb-4">{pkg.tagline}</p>
              <PriceBlock pkg={pkg} currency={currency} interval={interval} meta={meta} />
              <ul className="space-y-2.5 mb-6 flex-grow">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    {feature.startsWith("Unused credits") ? <RefreshCw className="w-4 h-4 text-violet-300 flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />}
                    <span className="text-white/70 text-xs leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => handleCheckout(pkg.id)} disabled={loading === pkg.id} data-testid={`pricing-cta-${pkg.id}`}
                className={`w-full rounded-full py-5 font-bold text-sm transition-all ${pkg.popular ? "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-lg" : pkg.id === "free" ? "bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white shadow-lg" : "bg-white/10 hover:bg-white/20 text-white border border-white/10"}`}>
                {loading === pkg.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{pkg.cta} <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
            </motion.div>
          ))}
        </div>

        <motion.p variants={fadeInUp} className="mt-6 text-center text-white/50 text-xs md:text-sm max-w-3xl mx-auto" data-testid="pricing-footnote">
          1 ad = one finished 15-second video ad ({CREDIT_COSTS.ad_15s} credits). 30-second ads use 2 ads. Premium quality uses 3x. Images are {CREDIT_COSTS.image} credits and 5-second B-roll clips are {CREDIT_COSTS.broll_5s}.
          Unused credits expire at the end of each billing month on Free, Starter, Creator and Growth; Pro and Agency roll unused credits over for one month.
        </motion.p>

        <motion.div variants={fadeInUp} className="mt-10 rounded-3xl p-6 md:p-8 ring-1 ring-rose-400/30 bg-gradient-to-br from-rose-500/[0.06] via-pink-500/[0.04] to-transparent" data-testid="enterprise-tier-card">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-rose-600 to-pink-700 text-white text-[10px] font-bold uppercase tracking-wider">Enterprise · Custom pricing</span>
                <h3 className="text-xl md:text-2xl font-bold text-white">Custom volume for large brands and agencies</h3>
              </div>
              <p className="text-white/55 text-sm mb-3 md:mb-0">Custom ads and credits per contract, unlimited seats, dedicated infrastructure and custom AI models. Unused credits roll over for one month.</p>
            </div>
            <Button asChild className="rounded-full px-6 py-5 bg-gradient-to-r from-rose-600 to-pink-700 hover:from-rose-500 hover:to-pink-600 text-white font-bold whitespace-nowrap" data-testid="pricing-cta-enterprise">
              <a href="#contact">Contact us <ArrowRight className="ml-2 w-4 h-4" /></a>
            </Button>
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="mt-6 rounded-2xl p-5 ring-1 ring-violet-400/20 bg-gradient-to-r from-violet-500/[0.05] to-indigo-500/[0.04] flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="topup-teaser">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-violet-500/20 flex items-center justify-center"><Sparkles className="w-5 h-5 text-violet-300" /></div>
          <div className="flex-1">
            <p className="text-white text-sm font-semibold">Need more credits this month?</p>
            <p className="text-white/50 text-xs">Top-ups are available in your dashboard. Top-up credits never expire.</p>
          </div>
          <Link to={user ? "/credits" : "/register?next=/credits"} className="shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/10 hover:bg-white/15 text-white text-xs font-bold" data-testid="topup-teaser-cta">
            {user ? "Buy credits" : "Start free to buy credits"} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
}
